import { Form, formProviderHOC } from '@sravni/cosago-react-library/lib/components';
import { useFormContext } from '@sravni/cosago-react-library/lib/hooks';
import type { FormComponentProps } from '@sravni/cosago-react-library/lib/types';
import { ADDITIONAL_PHONE_OWNER_VALUES } from '@sravni/creditselection-customer-utils/lib/utils/customers';
import { useIsMobile } from '@sravni/react-utils';
import type { FC } from 'react';
import React, { useEffect } from 'react';

import { LABELS } from 'constants/labels';

import { sendEventShowStep } from 'shared/lib/sendGAEvents';
import { useCommonFormBodyHooks } from 'shared/lib/useCommonFormBodyHooks';
import type { TCommonAdditionalFormProps } from 'shared/types';
import { FormStickyButton } from 'shared/ui';
import { FormBlockLabel } from 'shared/ui/FormBlockLabel';
import { FormWithControlledNavigation } from 'shared/ui/FormWithControlledNavigation';

import { useSendFieldErrorAnalyticsEvent } from 'entities/creditForm/lib/useSendFieldErrorAnalyticsEvent';
import type { TAdditionalDataFormFields } from 'src/client/entities/creditForm';
import { useDevFillMockDataTool } from 'src/client/entities/creditForm/lib/useDevFillMockDataTool';

import { AdditionalInfoTexts } from 'features/UpdateAdditionalInfo';
import { FormFieldsNavigationSequence } from 'features/UpdateAdditionalInfo/AdditionalInfo.config';
import { MoreFieldsAlert } from 'features/UpdateAdditionalInfo/ui/MoreFieldsAlert/MoreFieldsAlert';

import { useAccordionHeaderParams } from '../../lib/useAccordionHeaderParams';

type TAdditionalInfoFormView = TCommonAdditionalFormProps & {
  shouldRestoreAdditionalData: boolean;
};

type Props = FormComponentProps<TAdditionalDataFormFields, TAdditionalInfoFormView>;

const AdditionalInfoFormView: FC<Props> = ({
  isDialog,
  onSubmit,
  NAMES,
  submitButtonText,
  FieldConstructor,
  isLoading,
  additionalProps,
}) => {
  const { setHeader, isFormForceOpened, formId, isAnyFormStepOpenedInPopup, analyticsOverrides } =
    additionalProps || {};
  const isMobile = useIsMobile();
  const isStickyButton = !isDialog && isMobile && !isAnyFormStepOpenedInPopup;
  const { watch } = useFormContext<TAdditionalDataFormFields>();

  const additionalPhoneOwnerValue = watch('additionalPhoneOwner.value');
  const isAdditionalPhoneOwnerNotMyNumber =
    additionalPhoneOwnerValue && additionalPhoneOwnerValue !== ADDITIONAL_PHONE_OWNER_VALUES.MY_NUMBER;

  const [title, subtitle] = useAccordionHeaderParams(watch(), {}, false);

  useEffect(() => {
    sendEventShowStep(5, 'Дополнительно', analyticsOverrides);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSendFieldErrorAnalyticsEvent<TAdditionalDataFormFields>(5, AdditionalInfoTexts, analyticsOverrides);
  useDevFillMockDataTool<TAdditionalDataFormFields>({
    type: 'additionalData',
    stepLabel: 'Дополнительно',
  });

  const { controls } = useCommonFormBodyHooks({
    currentControls: FormFieldsNavigationSequence,
    isFormForceOpened,
    headerData: {
      title,
      subtitle,
    },
    setHeader,
  });

  return (
    <FormWithControlledNavigation
      onSubmit={onSubmit}
      controlElement={FieldConstructor}
      isDialog={isDialog}
      isLoading={isLoading}
      submitButtonText={submitButtonText}
      navigationControls={controls}
      formId={formId}
      withSubmitButtons={!isStickyButton}
    >
      <Form.Block>
        <Form.Row colWidths={[12]}>
          <MoreFieldsAlert />
        </Form.Row>
        <Form.Row>
          <FieldConstructor type={NAMES.education} />
          <FieldConstructor type={NAMES.maritalStatus} />
          <FieldConstructor type={NAMES.numberOfDependents} />
        </Form.Row>
        <Form.Row>
          <FieldConstructor type={NAMES.snils} />
        </Form.Row>
        <FormBlockLabel>{LABELS.AdditionalPhoneLabel}</FormBlockLabel>
        <Form.Row>
          <FieldConstructor type={NAMES.additionalPhone} />
          <FieldConstructor type={NAMES.additionalPhoneOwner} />
        </Form.Row>
        {isAdditionalPhoneOwnerNotMyNumber && (
          <Form.Row colWidths={[12]}>
            <FieldConstructor type={NAMES.additionalPhoneOwnerFullName} />
          </Form.Row>
        )}
        <FormBlockLabel>{LABELS.PropertyAvailabilityLabel}</FormBlockLabel>
        <Form.Row>
          <FieldConstructor type={NAMES.propertyTypeAvailability} />
        </Form.Row>
        <Form.Row>
          <FieldConstructor type={NAMES.carAvailability} />
        </Form.Row>
      </Form.Block>

      <FormStickyButton
        onClick={onSubmit}
        enabled={isStickyButton}
        isLoading={isLoading}
      >
        {submitButtonText}
      </FormStickyButton>
    </FormWithControlledNavigation>
  );
};

export const AdditionalInfoViewHoc = formProviderHOC<TAdditionalDataFormFields, TAdditionalInfoFormView>(
  AdditionalInfoFormView,
);
